/**
 * ExamenFormDialog - Crear/Editar tipo de examen
 */

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import { useExamenTipo, useCreateExamenTipo, useUpdateExamenTipo } from "@/hooks/use-ordenes";
import { useTiposContenedor } from "@/hooks/use-muestras";

const AREAS = [
  "HEMATOLOGIA",
  "QUIMICA",
  "SEROLOGIA",
  "INMUNOLOGIA",
  "URINALISIS",
  "COAGULACION",
  "MICROBIOLOGIA",
  "PARASITOLOGIA",
  "HORMONAS",
  "MARCADORES",
  "DROGAS",
  "ESPECIAL",
] as const;

const RESULT_TYPES = ["NUMERICO", "TEXTO", "OPCIONES", "FORMULA"] as const;

const formSchema = z.object({
  codigo: z.string().min(1, "Requerido").max(20),
  nombre: z.string().min(1, "Requerido").max(200),
  nombre_corto: z.string().max(50).default(""),
  area: z.enum(AREAS),
  unidad: z.string().max(30).default(""),
  precio: z.coerce.number().min(0).default(0),
  tipo_resultado: z.enum(RESULT_TYPES).default("NUMERICO"),
  requiere_ayuno: z.boolean().default(false),
  instrucciones_paciente: z.string().default(""),
  notas_internas: z.string().default(""),
  // Campos preanalíticos (task 1.6 / 11.8)
  tipo_contenedor_default: z.coerce.number().nullable().default(null),
  volumen_minimo_ml: z.coerce.number().nullable().default(null),
  condiciones_especiales: z.string().default(""),
  // Tiempo estimado de entrega (issue #99) — reemplaza dias_resultado
  dias_para_resultado: z.coerce.number().min(0).nullable().default(0),
  horas_para_resultado: z.coerce.number().min(0).max(23).nullable().default(0),
});

type FormValues = z.infer<typeof formSchema>;

interface ExamenFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examenId: number | null;
  onSuccess: (isNew: boolean) => void;
}

export function ExamenFormDialog({
  open,
  onOpenChange,
  examenId,
  onSuccess,
}: ExamenFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isNew = examenId === null;

  const { data: examen } = useExamenTipo(examenId || 0, { enabled: !!examenId && open });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      codigo: "",
      nombre: "",
      nombre_corto: "",
      area: "HEMATOLOGIA" as const,
      unidad: "",
      precio: 0,
      tipo_resultado: "NUMERICO" as const,
      requiere_ayuno: false,
      instrucciones_paciente: "",
      notas_internas: "",
      tipo_contenedor_default: null,
      volumen_minimo_ml: null,
      condiciones_especiales: "",
      dias_para_resultado: 0,
      horas_para_resultado: 0,
    },
  });

  const { data: tiposContenedorData } = useTiposContenedor();

  // Populate form when editing
  useEffect(() => {
    if (examen && !isNew) {
      form.reset({
        codigo: examen.codigo,
        nombre: examen.nombre,
        nombre_corto: examen.nombre_corto || "",
        area: examen.area,
        unidad: examen.unidad || "",
        precio: Number(examen.precio),
        tipo_resultado: examen.tipo_resultado,
        requiere_ayuno: examen.requiere_ayuno,
        instrucciones_paciente: examen.instrucciones_paciente || "",
        notas_internas: examen.notas_internas || "",
        tipo_contenedor_default: examen.tipo_contenedor_default ?? null,
        volumen_minimo_ml: examen.volumen_minimo_ml ? Number(examen.volumen_minimo_ml) : null,
        condiciones_especiales: examen.condiciones_especiales || "",
        dias_para_resultado: examen.dias_para_resultado ?? 0,
        horas_para_resultado: examen.horas_para_resultado ?? 0,
      });
    } else if (isNew && open) {
      form.reset();
    }
  }, [examen, isNew, open, form]);

  const createMutation = useCreateExamenTipo({
    onSuccess: () => onSuccess(true),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateMutation = useUpdateExamenTipo({
    onSuccess: () => onSuccess(false),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const onSubmit = (values: FormValues) => {
    if (isNew) {
      createMutation.mutate(values);
    } else {
      updateMutation.mutate({ id: examenId!, data: values });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t("ordenes.examenes.newExam") : t("ordenes.examenes.editExam")}
          </DialogTitle>
          <DialogDescription>
            {isNew ? t("ordenes.examenes.newExamDesc") : t("ordenes.examenes.editExamDesc")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="codigo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.code")} *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.area")} *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {AREAS.map((a) => (
                          <SelectItem key={a} value={a}>
                            {t(`ordenes.examenes.areas.${a}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="nombre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.examenes.fields.name")} *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nombre_corto"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.examenes.fields.shortName")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="unidad"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.unit")}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="mg/dL" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="precio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.price")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        value={field.value?.toString() || ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : 0)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Tiempo estimado de entrega — Issue #99 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="dias_para_resultado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.dias_para_resultado")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : 0)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="horas_para_resultado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.horas_para_resultado")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        placeholder="0"
                        value={field.value != null ? String(field.value) : ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : 0)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="tipo_resultado"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.resultType")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RESULT_TYPES.map((rt) => (
                          <SelectItem key={rt} value={rt}>
                            {t(`ordenes.examenes.resultTypes.${rt}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="requiere_ayuno"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3 pt-8">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0">
                      {t("ordenes.examenes.fields.requiresFasting")}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="instrucciones_paciente"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.examenes.fields.patientInstructions")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notas_internas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.examenes.fields.internalNotes")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Campos preanalíticos — M4.4, M4.5 */}
            <div className="border-t pt-4 space-y-4">
              <p className="text-sm font-medium text-muted-foreground">
                {t("ordenes.examenes.fields.preanalitico_titulo")}
              </p>

              <FormField
                control={form.control}
                name="tipo_contenedor_default"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.tipo_contenedor")}</FormLabel>
                    <Select
                      onValueChange={(val) => field.onChange(val === "none" ? null : Number(val))}
                      value={field.value != null ? String(field.value) : "none"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t("ordenes.examenes.fields.tipo_contenedor_placeholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">— {t("common.none")} —</SelectItem>
                        {(tiposContenedorData?.results ?? []).map((tc) => (
                          <SelectItem key={tc.id} value={String(tc.id)}>
                            {tc.nombre} ({tc.color})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="volumen_minimo_ml"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.volumen_minimo")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        placeholder="ej: 2.00"
                        value={field.value != null ? String(field.value) : ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : null)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="condiciones_especiales"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.examenes.fields.condiciones_especiales")}</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={2}
                        placeholder={t(
                          "ordenes.examenes.fields.condiciones_especiales_placeholder"
                        )}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("common.saving") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
